import { LeaderboardAdUnit } from "@/components/AdUnit";
import LoadingPlayersList from "@/components/organisms/PlayersList/LoadingPlayersList";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import Pagination from "../../components/molecules/Pagination/Pagination";
import { PlayerList } from "../../components/organisms/PlayersList/PlayersList";
import {
  PlayersSortByType,
  PlayersToolbar,
} from "../../components/organisms/PlayersToolbar/PlayersToolbar";
import { fireGTMEvent } from "../../functions/fireGTMEvent";
import useDebounce from "../../hooks/useDebounce";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useSticky } from "../../hooks/useSticky";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { showModal } from "../../redux/message/messageSlice";
import {
  MetaRatingsPlayer,
  PlayerRole,
  clearAllFilters,
  clearFilter,
  fetchPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "../../redux/players/playersSlice";
import { AppDispatch } from "../../redux/store";
import { canUseClubImportSelector } from "../../redux/user/userSlice";
import { BREAKPOINTS } from "../../settings";
import FiltersSidebar from "./FiltersSidebar/FiltersSidebar";
import { SelectPlayerRole } from "./SelectPlayerRole/SelectPlayerRole";

export type QueryParams = { key: string; values: (number | string)[] };

type DropdownType = "playerRole" | "filters" | "none";

export const PlayersPage = () => {
  const maxPageWidth = "max-w-[1672px]";
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  const dispatch = useDispatch<AppDispatch>();

  let { players, totalPages, playersStatus, playerFilters } =
    useSelector(playersSelector);
  const [searchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState<PlayersSortByType>(
    searchParams.size > 0 ? "meta-rating" : "date-added",
  );
  const [didLoadSortBy, setDidLoadSortBy] =
    useState<PlayersSortByType>("meta-rating");
  const { resourceId } = useParams();
  const { playerRoles } = useSelector(playersSelector);
  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const queryParams = useSelector(playerFiltersAsQueryParamsSelector);
  const stableQueryParams = useMemo(
    () => queryParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queryParams)],
  );
  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const [page, setPage] = useState(1);
  const [playerRole, setPlayerRole] = useState<PlayerRole>(undefined);
  const [dropdown, setDropdown] = useState<DropdownType>("none");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [player, setPlayer] = useState<
    MetaRatingsPlayer & { show3Chem: boolean }
  >();
  // const [players, setPlayers] = useState<MetaRatingsPlayer[]>(currentPlayers);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const refPlayerRole = useOutsideClick(
    () => dropdown === "playerRole" && setDropdown("none"),
  );

  const refFilters = useOutsideClick(
    () => dropdown === "filters" && setDropdown("none"),
  );

  const handleDropdown = (inputDropdown: DropdownType) => {
    setDropdown(dropdown === inputDropdown ? "none" : inputDropdown);
  };

  // Use the URL filters hook
  const { hasInitializedFromUrl, playerRolesReady, updateUrlFromFilters } =
    useUrlFilters({
      playerRoles,
      playerFilters,
      onPlayerRoleChange: setPlayerRole,
    });

  // Update URL when filters change (only after initialization and playerRoles are ready)
  useEffect(() => {
    if (hasInitializedFromUrl && playerRolesReady && !resourceId) {
      updateUrlFromFilters(
        playerRole ?? "any",
        ownedOrEvolvedFilters,
        stableQueryParams,
      );
    }
  }, [
    playerRole,
    ownedOrEvolvedFilters,
    stableQueryParams,
    updateUrlFromFilters,
    hasInitializedFromUrl,
    playerRolesReady,
    resourceId,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearAllFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [stableQueryParams]);

  useEffect(() => {
    const player = players.find(
      (p) =>
        p.eaPlayerId === +resourceId ||
        (p.resourceId === +resourceId && !p.eaPlayerId),
    );
    setPlayer({
      ...player,
      show3Chem: didLoadSortBy === "meta-rating-3-chem",
    });
  }, [players, resourceId, playerRole]);

  const handleSelectPlayerRole = (pr: PlayerRole) => {
    fireGTMEvent("added_player_role");
    setPage(1);
    setPlayerRole(pr);
    if (sortBy === "date-added") {
      setSortBy("meta-rating");
    }
  };

  useEffect(() => {
    dispatch(
      fetchPlayers({
        page,
        sort: sortBy,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        playerRoleId: playerRole?.id,
        queryParams: stableQueryParams,
        evolutions: false,
        searchText: debouncedSearchTerm,
      }),
    ).then(() => {
      if (didLoadSortBy !== sortBy) {
        setDidLoadSortBy(sortBy);
      }
    });
  }, [
    dispatch,
    page,
    sortBy,
    playerRole,
    stableQueryParams,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
  ]);

  const handleSearch = (searchTerm: string) => {
    setPage(1);
    if (sortBy === "date-added") {
      setSortBy("meta-rating");
    }
    setSearchTerm(searchTerm);
  };

  return resourceId ? (
    <Outlet context={player} />
  ) : (
    <main className="flex flex-col w-full overflow-y-auto bg-page-gradient">
      <div ref={refTopBar} />
      <>
        <PageHeader
          title={"EA FC 25 Players and Meta Ratings Database"}
          isMobile={isMobile}
          maxWidth={maxPageWidth}
          color="bg-[#00425F]"
          helmetTitle="EA FC 25 Players and Meta Ratings - EasySBC"
          helmetContent="Explore our EA FC 25 Players Database with exclusive Meta Ratings for over 20,000 cards. Find the best players for your squad and see what chemstyle to use."
          helmetLink="https://www.easysbc.io/players"
        />
        <div
          className={clsx(
            "flex flex-col gap-y-1 desktop:gap-y-4 mx-auto items-center desktop:px-8 desktop:mt-4 w-full",
            maxPageWidth,
          )}
        >
          <PlayersToolbar
            searchTerm={searchTerm}
            setSearchTerm={handleSearch}
            dropdown={dropdown}
            handleDropdown={handleDropdown}
            status={playersStatus}
            isTablet={isTouchDevice}
            isMobile={isMobile}
            hasFilters={hasFilters}
            sortBy={sortBy}
            setSortBy={(v) => {
              setSortBy(v);
              fireGTMEvent("sorted_players");
            }}
            playerRole={playerRole}
            playerFilters={playerFilters}
            onUpdateUsingOwnedPlayers={(f) => {
              if (canUseClubImport) {
                dispatch(updateFilter(f));
                if (sortBy === "date-added") {
                  setSortBy("meta-rating");
                }
              } else {
                dispatch(
                  showModal({
                    modalType: "premium-feature",
                    premiumFeatureHook: "see-owned-players",
                  }),
                );
              }
            }}
            isPlayersPage
          />
          <div className="w-full -mt-2 desktop:-mt-4">
            <section
              className={clsx(
                "w-full mx-auto text-white p-3 mobile:px-3 px-2 rounded-b-xl flex flex-col gap-y-1 bg-primary-dark",
              )}
            >
              {(playersStatus === "pending" || playersStatus === "idle") &&
              (players.length === 0 || isTouchDevice) ? (
                <LoadingPlayersList />
              ) : players.length === 0 ? (
                <div className="text-center text-white text-sm">
                  No players found
                </div>
              ) : (
                <PlayerList
                  players={players}
                  show3Chem={didLoadSortBy === "meta-rating-3-chem"}
                  playerRoleId={playerRole?.id}
                />
              )}
            </section>
          </div>
          {players.length > 0 && (
            <div className="flex justify-end w-full mb-8 md-lg:pr-8">
              <Pagination
                currentPage={page}
                totalAmountOfPages={totalPages}
                changePage={(p) => {
                  setPage(p);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          )}
          {dropdown === "filters" ? (
            <div ref={refFilters}>
              <FiltersSidebar
                isTablet={isTouchDevice}
                handleClose={() => handleDropdown("none")}
                playerFilters={playerFilters}
                disabledFilters={[]}
                onClearFilter={(key) => dispatch(clearFilter(key))}
                onUpdateFilter={(f) => {
                  fireGTMEvent("added_filter");
                  dispatch(updateFilter(f));
                  if (sortBy === "date-added") {
                    setSortBy("meta-rating");
                  }
                }}
                hasActiveFilters={hasFilters}
                onClearFilters={() => dispatch(clearAllFilters())}
                parentRoute="players"
              />
            </div>
          ) : dropdown === "playerRole" ? (
            <div ref={refPlayerRole}>
              <SelectPlayerRole
                playerRoles={playerRoles}
                selectedPlayerRole={playerRole}
                handleSelectPlayerRole={handleSelectPlayerRole}
                handleOnClose={() => handleDropdown("none")}
                shouldStickToTop={scrollPastTopBar}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      </>
    </main>
  );
};

export default PlayersPage;
